"use client";

import { type ChangeEvent, useRef, useState } from "react";
import {
  ErrorNotice,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_PRIMARY, BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import type { AbstractScreeningToolStrings } from "@/components/tools/abstract-screening-tool";
import { decodeRisBytes } from "@/lib/tool-kit/identifiers/ris-check";

/** Step one: paste or choose the records to screen. */
export function ScreeningLoader({
  strings,
  problem,
  onLoad,
}: {
  strings: AbstractScreeningToolStrings;
  problem: string | null;
  onLoad: (text: string) => void;
}) {
  const [text, setText] = useState("");
  const upload = useRef<HTMLInputElement>(null);

  async function readFile(event: ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0];
    event.target.value = "";
    if (chosen) {
      const decoded = decodeRisBytes(
        new Uint8Array(await chosen.arrayBuffer())
      );
      setText(decoded.text);
      onLoad(decoded.text);
    }
  }

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="ast-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_PRIMARY}
          disabled={text.trim() === ""}
          onClick={() => onLoad(text)}
          type="button"
        >
          {strings.load}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => upload.current?.click()}
          type="button"
        >
          {strings.upload}
        </button>
        <label className="sr-only" htmlFor="ast-file">
          {strings.uploadLabel}
        </label>
        <input
          accept=".ris,.csv,.tsv,.txt,.nbib,.xml,text/*"
          className="hidden"
          id="ast-file"
          onChange={readFile}
          ref={upload}
          tabIndex={-1}
          type="file"
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setText(strings.sample);
            onLoad(strings.sample);
          }}
          type="button"
        >
          {strings.trySample}
        </button>
      </div>
      {problem ? (
        <ErrorNotice>
          {(strings.problems as Record<string, string>)[problem] ?? problem}
        </ErrorNotice>
      ) : (
        <Notice>{strings.problems.empty}</Notice>
      )}
    </section>
  );
}
